import React, { useState } from 'react';
import { Card, Skeleton, Select, Empty } from 'antd';
import { ResponsivePie } from '@nivo/pie';
import { useRequest } from 'ahooks';
import { getCtaDistribution } from '../../services/dashboardService.js';

export default function CtaDistributionCard({ dateParams }) {
  const [type, setType] = useState('ALL');
  const [hiddenLevels, setHiddenLevels] = useState([]);
  
  const params = React.useMemo(
    () => ({ ...(dateParams || {}), type }),
    [dateParams, type]
  );
  
  const { data, loading } = useRequest(() => getCtaDistribution(params), {
    refreshDeps: [params.type, params.start, params.end, params.companyId],
  });

  const dist = React.useMemo(() => data?.data || data || [], [data]);
  
  const filteredDist = React.useMemo(() => {
    return dist.map(d => ({
      id: d.level,
      label: d.level,
      value: hiddenLevels.includes(d.level) ? 0 : d.count,
      originalValue: d.count,
    }));
  }, [dist, hiddenLevels]);

  const total = React.useMemo(() => {
    return dist.reduce((acc, d) => {
      if (hiddenLevels.includes(d.level)) return acc;
      return acc + d.count;
    }, 0);
  }, [dist, hiddenLevels]);

  const handleLegendClick = (level) => {
    setHiddenLevels(prev => {
      if (prev.includes(level)) {
        return prev.filter(l => l !== level);
      } else {
        return [...prev, level];
      }
    });
  };

  const colorMap = {
    Minimal: '#34a853',
    Low: '#fbbc04',
    Moderate: '#f29900',
    High: '#ea4335',
  };

  const getLegendColor = (level) => {
    const isHidden = hiddenLevels.includes(level);
    return isHidden ? '#d9d9d9' : (colorMap[level] || '#999');
  };

  return (
    <Card
      title="CTA Distribution"
      extra={
        <Select
          size="small"
          value={type}
          onChange={setType}
          options={[
            { value: 'ALL', label: 'All Types' },
            { value: 'URL', label: 'URL' },
            { value: 'BankAccount', label: 'BankAccount' },
            { value: 'Phone', label: 'Phone' },
            { value: 'Email', label: 'Email' },
            { value: 'Card', label: 'Card' },
            { value: 'other', label: 'Other' },
          ]}
        />
      }
    >
      {loading ? (
        <Skeleton active />
      ) : (
        <div style={{ height: 240, position: 'relative' }}>
          {!filteredDist || filteredDist.length === 0 ? (
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              overflow: 'hidden' 
            }}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <ResponsivePie
              data={filteredDist}
              innerRadius={0}
              padAngle={1}
              colors={({ id }) => {
                if (hiddenLevels.includes(id)) return '#e0e0e0';
                return colorMap[id] || '#999';
              }}
              layers={[
                'arcs',
                'arcLabels',
                'arcLinkLabels',
                'legends',
              ]}
              arcOpacity={({ datum }) =>
                hiddenLevels.includes(datum.id) ? 0.15 : 1
              }
              enableArcLinkLabels={false}
              arcLabelsSkipAngle={360}
              onClick={(datum) => {
                handleLegendClick(datum.id);
              }}

              onMouseEnter={(datum, event) => {
                event.currentTarget.style.cursor = 'pointer';
              }}
              tooltip={({ datum }) => {
                const realValue = datum.data.originalValue;
                const percentage = total === 0 ? 0 : (realValue / total) * 100;

                return (
                  <div style={{ padding: 8, background: '#fff' }}>
                    <strong>{datum.id}</strong>: {realValue}
                    <span style={{ marginLeft: 6 }}>
                      ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                );
              }}
              margin={{ top: 10, right: 150, bottom: 10, left: 50 }}
              legends={[
                {
                  anchor: 'right',
                  direction: 'column',
                  translateX: 160,
                  itemWidth: 120,
                  itemHeight: 26,
                  itemsSpacing: 8,
                  symbolSize: 14,
                  symbolShape: ({
                    x,
                    y,
                    size,
                    fill,
                    borderWidth,
                    borderColor,
                  }) => (
                    <rect
                      x={x}
                      y={y}
                      rx={3}
                      ry={3}
                      width={size}
                      height={size}
                      fill={fill}
                      stroke={borderColor}
                      strokeWidth={borderWidth}
                    />
                  ),

                  onClick: (datum) => {
                    handleLegendClick(datum.id);
                  },

                  onMouseEnter: (datum, event) => {
                    event.currentTarget.style.cursor = 'pointer';
                  },

                  itemTextColor: (datum) => {
                    return hiddenLevels.includes(datum.id) ? '#bfbfbf' : '#333';
                  },
                  itemOpacity: (datum) => {
                    return hiddenLevels.includes(datum.id) ? 0.5 : 1;
                  },

                  fill: (datum) => {
                    return getLegendColor(datum.id);
                  },
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemTextColor: (datum) => {
                          return hiddenLevels.includes(datum.id) ? '#a6a6a6' : '#000';
                        },
                        itemBackground: '#f5f5f5',
                      },
                    },
                  ],
                  //legend data
                  data: dist.map(d => ({
                    id: d.level,
                    label: d.level,
                    color: getLegendColor(d.level),
                  })),
                },
              ]}
            />
          )}
        </div>
      )}
    </Card>
  );
}
